<template>
  <v-container fluid>
    <v-row v-if="!convertCurrency" justify="center" align="center" class="mb-4 mt-2">
      <v-col cols="auto">
        <v-btn-toggle v-model="selectedCurrency" mandatory color="primary" class="elevation-2" @change="onCurrencyChange">
          <v-btn v-for="(item, i) of currencies" :key="i" :value="item.id">
            {{ item.code }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
    <v-row align="center" class="mb-2">
      <v-col cols="auto" class="pt-6">
        <v-switch
          v-model="convertCurrency"
          :label="convertCurrency == false ? 'Moneda Base' : 'Convertir Moneda'"
          hide-details
          dense
          color="primary"
          class="mt-0"
          @change="onConvertCurrencyChange"
        ></v-switch>
      </v-col>
      <v-col v-if="convertCurrency" cols="12" md="2">
        <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Moneda Destino</div>
        <v-autocomplete
          dense
          outlined
          v-model="selectedConvertCurrency"
          :items="allCurrencies"
          label="Divisa"
          :rules="[(v) => !!v || 'Moneda es requerida']"
          color="primary"
          prepend-inner-icon="mdi-currency-usd"
          item-text="code"
          item-value="id"
          hide-details
          required
          @change="onConvertCurrencyChange"
          class="custom-field"
        ></v-autocomplete>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn
          color="primary"
          outlined
          @click="openPdfOptions"
          :loading="generatingPdf"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          Reporte de antigüedad PDF
        </v-btn>
      </v-col>
    </v-row>
    <!-- Skeleton Loader -->
    <template v-if="loading">
      <v-row>
        <v-col cols="12" md="3" v-for="n in 4" :key="'sk-kpi-'+n">
          <v-skeleton-loader type="card" height="100"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="12" md="8">
          <v-skeleton-loader type="card" height="400"></v-skeleton-loader>
        </v-col>
        <v-col cols="12" md="4">
          <v-skeleton-loader type="card" height="400"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <v-skeleton-loader type="card" height="450"></v-skeleton-loader>
        </v-col>
        <v-col cols="12" md="6">
          <v-skeleton-loader type="card" height="450"></v-skeleton-loader>
        </v-col>
      </v-row>
    </template>

    <!-- Actual Content -->
    <template v-else>
      <!-- KPIs Cards - 5 columns -->
      <v-row>
        <v-col cols="12" sm="6" md v-for="(kpi, index) in kpis" :key="index">
          <v-card outlined>
            <v-card-text class="text-center pa-3">
              <v-icon :color="kpi.color" size="36">{{ kpi.icon }}</v-icon>
              <div class="text-h6 font-weight-bold mt-1" :class="`${kpi.color}--text`">
                {{ kpi.value }}
              </div>
              <div class="text-caption grey--text">{{ kpi.title }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <!-- Aging Report - Bar Chart -->
        <v-col cols="12" md="8">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-chart-bar</v-icon>
              Antigüedad de Saldos (Aging Report)
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="350" :options="agingChartOptions" :series="agingChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Status Distribution - Donut Chart -->
        <v-col cols="12" md="4">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-chart-donut</v-icon>
              Distribución por Estado
            </v-card-title>
            <v-card-text>
              <apexchart type="donut" height="350" :options="statusChartOptions" :series="statusChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- NEW: Aging Stacked Bar by Entity -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-chart-bar-stacked</v-icon>
              Antigüedad por {{ entityLabelSingular }} (Top 10)
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="380" :options="stackedAgingOptions" :series="stackedAgingSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <!-- Top Suppliers - Horizontal Bar -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-account-group</v-icon>
              Top 10 {{ entityLabel }} por Saldo Pendiente
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="400" :options="topSuppliersChartOptions" :series="topSuppliersChartSeries">
              </apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- NEW: Pareto / Concentration Risk -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-chart-areaspline</v-icon>
              Concentración de Riesgo (Pareto 80/20)
            </v-card-title>
            <v-card-text>
              <apexchart type="line" height="400" :options="paretoChartOptions" :series="paretoChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <!-- Payment Trend - Line Chart -->
        <v-col cols="12" md="6">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-chart-line</v-icon>
              Tendencia de Pagos (Últimos 6 Meses)
            </v-card-title>
            <v-card-text>
              <apexchart type="line" height="400" :options="trendChartOptions" :series="trendChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- DPO/DSO Gauge -->
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-speedometer</v-icon>
              {{ moduleType === 'cxc' ? 'DSO' : 'DPO' }}
            </v-card-title>
            <v-card-text>
              <apexchart type="radialBar" height="280" :options="dpoChartOptions" :series="dpoChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Currency Distribution -->
        <v-col cols="12" md="3">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-currency-usd</v-icon>
              Monedas
            </v-card-title>
            <v-card-text>
              <apexchart type="pie" height="280" :options="currencyChartOptions" :series="currencyChartSeries">
              </apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- NEW: Location Distribution -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-map-marker-multiple</v-icon>
              Distribución por Ubicación (Location)
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="350" :options="locationChartOptions" :series="locationChartSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Drill-down Data Table -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-table-large</v-icon>
              Detalle de Antigüedad por {{ entityLabelSingular }}
              <v-spacer></v-spacer>
              <v-text-field
                v-model="tableSearch"
                append-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                dense
                outlined
                class="ml-4"
                style="max-width: 300px"
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="agingTableHeaders"
              :items="agingTableItems"
              :search="tableSearch"
              :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
              dense
              class="elevation-0"
              sort-by="total"
              sort-desc
            >
              <template v-slot:item.total="{ item }">
                <span class="font-weight-bold">{{ currencyFormat(item.total) }}</span>
              </template>
              <template v-slot:item.a0_30="{ item }">
                <span :class="item.a0_30 > 0 ? 'success--text' : 'grey--text'">{{ currencyFormat(item.a0_30) }}</span>
              </template>
              <template v-slot:item.a31_60="{ item }">
                <span :class="item.a31_60 > 0 ? 'info--text' : 'grey--text'">{{ currencyFormat(item.a31_60) }}</span>
              </template>
              <template v-slot:item.a61_90="{ item }">
                <span :class="item.a61_90 > 0 ? 'warning--text' : 'grey--text'">{{ currencyFormat(item.a61_90) }}</span>
              </template>
              <template v-slot:item.a90plus="{ item }">
                <span :class="item.a90plus > 0 ? 'error--text' : 'grey--text'">{{ currencyFormat(item.a90plus) }}</span>
              </template>
              <template v-slot:item.pct_overdue="{ item }">
                <v-chip :color="item.pct_overdue > 50 ? 'error' : item.pct_overdue > 25 ? 'warning' : 'success'" dark x-small>
                  {{ item.pct_overdue }}%
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Opciones del reporte PDF -->
    <v-dialog v-model="pdfOptionsDialog" max-width="720" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-file-pdf-box</v-icon>Reporte de antigüedad de saldos
          <v-spacer></v-spacer>
          <v-btn icon @click="pdfOptionsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-caption grey--text mb-3">
            Se aplican los filtros actuales del dashboard: <b>{{ businessUnitName || 'Todas las unidades' }}</b>
            · {{ locationName || 'Todas las sucursales' }} · Moneda {{ displayCurrencyCode() }}{{ convertCurrency ? ' (convertida)' : '' }}.
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-menu v-model="pdfOptions.menuAsOf" :close-on-content-click="false" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="fmtDateDMY(pdfOptions.as_of)" label="Fecha de corte" readonly outlined dense hide-details
                    v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-end"></v-text-field>
                </template>
                <v-date-picker v-model="pdfOptions.as_of" locale="es" color="primary" no-title @input="pdfOptions.menuAsOf = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="pdfOptions.date_type" :items="pdfDateTypes" label="Tipo de fecha" outlined dense hide-details
                prepend-inner-icon="mdi-calendar-question" hint="Qué fecha de la factura se compara con el corte"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="pdfOptions.due_days" :items="pdfDueDays" label="Tramos de vencimiento" outlined dense hide-details
                prepend-inner-icon="mdi-timeline-clock-outline"></v-select>
            </v-col>
            <v-col cols="12" sm="6" v-if="moduleType === 'cxp'">
              <v-select v-model="pdfOptions.supplier_type_id" :items="[{ id: null, name: 'Todos' }, ...supplierTypes]" item-text="name" item-value="id"
                label="Tipo de suplidor" outlined dense hide-details prepend-inner-icon="mdi-account-group-outline" :loading="supplierTypesLoading"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="pdfOptions.language" :items="[{ value: 'es', text: 'Español' }, { value: 'en', text: 'Inglés' }]"
                label="Idioma del reporte" outlined dense hide-details prepend-inner-icon="mdi-translate"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="pdfOptions.top_n" :items="[5, 10, 20]" label="Concentración: top N" outlined dense hide-details
                prepend-inner-icon="mdi-podium"></v-select>
            </v-col>
            <v-col cols="12">
              <v-checkbox v-model="pdfOptions.include_detail" dense hide-details label="Incluir detalle por factura (agrupado por suplidor)"></v-checkbox>
            </v-col>
          </v-row>
          <div class="text-caption grey--text mt-3">
            El saldo se calcula a la fecha de corte: asientos en la cuenta por pagar hasta el corte menos pagos vigentes al corte.
            Con detalle, el PDF puede ser largo si el corte incluye muchas facturas.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="pdfOptionsDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="generatingPdf" @click="generateAgingPDF">
            <v-icon left small>mdi-file-pdf-box</v-icon>Generar PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PDF Preview Dialog -->
    <v-dialog v-model="pdfDialog" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary" dense>
          <v-btn icon dark @click="closePdfDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Aging Report PDF</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="downloadPdf" v-if="pdfUrl">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0" style="height: calc(100vh - 48px);">
          <iframe
            v-if="pdfUrl"
            :src="pdfUrl"
            width="100%"
            height="100%"
            style="border: none;"
          ></iframe>
          <div v-else class="d-flex justify-center align-center" style="height: 100%;">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'AgingReportDashboard',
  props: {
    // Ids para el reporte PDF (si no vienen, se extraen del texto `filters` como respaldo)
    locationId: { type: [Number, String], default: 0 },
    currencyId: { type: [Number, String], default: null },
    locationAccess: { type: Array, default: () => [] },
    data: {
      type: Object,
      default: () => ({})
    },
    moduleType: {
      type: String,
      default: 'cxp',
      validator: (value) => ['cxp', 'cxc'].includes(value)
    },
    businessUnitName: {
      type: String,
      default: ''
    },
    currencyName: {
      type: String,
      default: 'USD'
    },
    reportDate: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    currencies: {
      type: Array,
      default: () => []
    },
    allCurrencies: {
      type: Array,
      default: () => []
    },
    filters: {
      type: String,
      default: ''
    },
    businessId: {
      type: [Number, String],
      default: 0
    },
    locationName: {
      type: String,
      default: ''
    },
    companyName: {
      type: String,
      default: 'GRUPO M'
    }
  },
  computed: {
    totalLabel() {
      return this.moduleType === 'cxc' ? 'Total Por Cobrar' : 'Total Por Pagar';
    },
    entityLabel() {
      return this.moduleType === 'cxc' ? 'Clientes' : 'Suplidores';
    },
    entityLabelSingular() {
      return this.moduleType === 'cxc' ? 'Cliente' : 'Suplidor';
    },
    activeEntityLabel() {
      return this.moduleType === 'cxc' ? 'Clientes Activos' : 'Suplidores Activos';
    }
  },
  data() {
    return {
      generatingPdf: false,
      pdfDialog: false,
      pdfUrl: null,
      pdfOptionsDialog: false,
      pdfOptions: { as_of: null, date_type: 1, due_days: '30,60,90', supplier_type_id: null, language: 'es', top_n: 10, include_detail: false, menuAsOf: false },
      pdfDateTypes: [
        { value: 1, text: 'Contable (fecha contable de la factura)' },
        { value: 2, text: 'Emisión (fecha de la factura)' },
        { value: 3, text: 'Vencimiento (fecha de vencimiento)' },
      ],
      pdfDueDays: [
        { value: '30,60,90', text: '1-30 · 31-60 · 61-90 · +90 días' },
        { value: '15,30,45', text: '1-15 · 16-30 · 31-45 · +45 días' },
        { value: '7,14,21', text: '1-7 · 8-14 · 15-21 · +21 días' },
      ],
      supplierTypes: [],
      supplierTypesLoading: false,
      selectedCurrency: null,
      convertCurrency: false,
      selectedConvertCurrency: null,
      tableSearch: '',
      agingTableHeaders: [
        { text: 'Nombre', value: 'name', width: '220px' },
        { text: 'Total', value: 'total', align: 'end' },
        { text: '0-30 Días', value: 'a0_30', align: 'end' },
        { text: '31-60 Días', value: 'a31_60', align: 'end' },
        { text: '61-90 Días', value: 'a61_90', align: 'end' },
        { text: '+90 Días', value: 'a90plus', align: 'end' },
        { text: '% Vencido', value: 'pct_overdue', align: 'center', width: '100px' },
      ],
      agingTableItems: [],
      kpis: [
        {
          title: '', // Will be set dynamically
          value: '$0.00',
          icon: 'mdi-cash-multiple',
          color: 'error'
        },
        {
          title: 'Facturas Vencidas',
          value: '0',
          icon: 'mdi-alert-circle',
          color: 'warning'
        },
        {
          title: '', // Will be set dynamically (DPO/DSO)
          value: '0 días',
          icon: 'mdi-calendar-clock',
          color: 'info'
        },
        {
          title: '', // Will be set dynamically
          value: '0',
          icon: 'mdi-account-group',
          color: 'success'
        },
        {
          title: '% Vencido',
          value: '0.00%',
          icon: 'mdi-percent-outline',
          color: 'deep-orange'
        }
      ],

      // Aging Report Chart
      agingChartSeries: [{
        name: 'Monto Pendiente',
        data: [0, 0, 0, 0, 0]
      }],
      agingChartOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: true
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"]
          }
        },
        colors: ['#00E396', '#008FFB', '#FEB019', '#FF4560', '#775DD0'],
        xaxis: {
          categories: ['0-30 días', '31-60 días', '61-90 días', '91-120 días', '+120 días'],
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Monto ($)'
          },
          labels: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US');
            }
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
          }
        }
      },

      // Status Distribution Chart
      statusChartSeries: [0, 0, 0],
      statusChartOptions: {
        chart: {
          type: 'donut'
        },
        labels: ['Vencidas', 'Por Vencer (7 días)', 'En Plazo'],
        colors: ['#FF4560', '#FEB019', '#00E396'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + '%';
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + ' facturas';
            }
          }
        }
      },

      // Top Suppliers Chart
      topSuppliersChartSeries: [{
        name: 'Saldo Pendiente',
        data: []
      }],
      topSuppliersChartOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '70%',
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return '$' + val.toLocaleString('en-US');
          },
          offsetX: 30,
          style: {
            fontSize: '10px',
            colors: ['#304758']
          }
        },
        colors: ['#008FFB'],
        xaxis: {
          categories: [],
          labels: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US');
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '11px'
            }
          }
        }
      },

      // Trend Chart
      trendChartSeries: [{
        name: 'Facturas Generadas',
        data: []
      }, {
        name: 'Pagos Realizados',
        data: []
      }],
      trendChartOptions: {
        chart: {
          type: 'line',
          toolbar: {
            show: true
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#FF4560', '#00E396'],
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '11px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Monto ($)'
          },
          labels: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US');
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
          }
        },
        legend: {
          position: 'top'
        }
      },

      // DPO Gauge
      dpoChartSeries: [0],
      dpoChartOptions: {
        chart: {
          type: 'radialBar'
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: '70%'
            },
            dataLabels: {
              name: {
                fontSize: '16px',
                color: '#888',
                offsetY: -10
              },
              value: {
                fontSize: '30px',
                color: '#111',
                formatter: function (val) {
                  return Math.round(val) + ' días';
                }
              }
            }
          }
        },
        colors: ['#008FFB'],
        labels: ['DPO Promedio'],
        stroke: {
          lineCap: 'round'
        }
      },

      // Currency Distribution
      currencyChartSeries: [],
      currencyChartOptions: {
        chart: {
          type: 'pie'
        },
        labels: [],
        colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return opts.w.config.labels[opts.seriesIndex] + ': ' + val.toFixed(1) + '%';
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
          }
        }
      },

      // Stacked Aging by Entity
      stackedAgingSeries: [],
      stackedAgingOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: { horizontal: true, barHeight: '65%' }
        },
        colors: ['#00E396', '#008FFB', '#FEB019', '#FF4560'],
        xaxis: {
          categories: [],
          labels: {
            formatter: function (val) {
              return '$' + (val / 1000).toFixed(0) + 'K';
            }
          }
        },
        yaxis: {
          labels: { style: { fontSize: '11px' } }
        },
        legend: { position: 'top' },
        dataLabels: { enabled: false },
        tooltip: {
          y: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
          }
        }
      },

      // Pareto / Concentration Risk
      paretoChartSeries: [],
      paretoChartOptions: {
        chart: {
          type: 'line',
          toolbar: { show: false }
        },
        stroke: { width: [0, 3], curve: 'smooth' },
        colors: ['#008FFB', '#FF4560'],
        plotOptions: {
          bar: { columnWidth: '60%' }
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
            style: { fontSize: '10px' },
            trim: true,
            maxHeight: 80
          }
        },
        yaxis: [
          {
            title: { text: 'Monto ($)' },
            labels: {
              formatter: function (val) {
                return '$' + (val / 1000).toFixed(0) + 'K';
              }
            }
          },
          {
            opposite: true,
            title: { text: '% Acumulado' },
            min: 0,
            max: 100,
            labels: {
              formatter: function (val) {
                return val.toFixed(0) + '%';
              }
            }
          }
        ],
        annotations: {
          yaxis: [{
            y: 80,
            yAxisIndex: 1,
            borderColor: '#FF4560',
            strokeDashArray: 4,
            label: {
              text: '80%',
              style: { color: '#FF4560', background: '#fff' }
            }
          }]
        },
        legend: { position: 'top' },
        tooltip: {
          shared: true,
          intersect: false
        }
      },

      // Location Distribution
      locationChartSeries: [{
        name: 'Saldo Pendiente',
        data: []
      }],
      locationChartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            distributed: true,
            dataLabels: { position: 'top' }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 0 });
          },
          offsetY: -20,
          style: { fontSize: '11px', colors: ['#304758'] }
        },
        colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A', '#26a69a', '#D10CE8'],
        xaxis: {
          categories: [],
          labels: { style: { fontSize: '11px' }, rotate: -35, trim: true, maxHeight: 80 }
        },
        yaxis: {
          title: { text: 'Monto ($)' },
          labels: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US');
            }
          }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: function (val) {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
          }
        }
      }
    };
  },
  watch: {
    data: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.updateCharts(newData);
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    updateCharts(data) {
      // Update KPI titles dynamically
      this.kpis[0].title = this.totalLabel;
      this.kpis[2].title = this.moduleType === 'cxc' ? 'Promedio DSO' : 'Promedio DPO';
      this.kpis[3].title = this.activeEntityLabel;

      // Update KPIs
      if (data.kpis) {
        this.kpis[0].value = this.currencyFormat(data.kpis.totalPending || 0);
        this.kpis[1].value = data.kpis.overdueCount || 0;
        this.kpis[2].value = (data.kpis.avgDPO || 0) + ' días';
        this.kpis[3].value = data.kpis.activeSuppliers || 0;

        // % Vencido KPI
        const totalPending = parseFloat(data.kpis.totalPending || 0);
        const overdueAmount = parseFloat(data.kpis.overdueAmount || 0);
        const pctOverdue = totalPending > 0 ? ((overdueAmount / totalPending) * 100).toFixed(2) : '0.00';
        this.kpis[4].value = pctOverdue + '%';
        this.kpis[4].color = parseFloat(pctOverdue) > 50 ? 'error' : parseFloat(pctOverdue) > 25 ? 'deep-orange' : 'success';
      }

      // Update Aging Chart
      if (data.aging) {
        this.agingChartSeries = [{
          name: 'Monto Pendiente',
          data: [
            data.aging.range0_30 || 0,
            data.aging.range31_60 || 0,
            data.aging.range61_90 || 0,
            data.aging.range91_120 || 0,
            data.aging.range120plus || 0
          ]
        }];
      }

      // Update Status Chart
      if (data.status) {
        this.statusChartSeries = [
          data.status.overdue || 0,
          data.status.upcoming || 0,
          data.status.onTime || 0
        ];
      }

      // Update Top Suppliers
      if (data.topSuppliers) {
        this.topSuppliersChartSeries = [{
          name: 'Saldo Pendiente',
          data: data.topSuppliers.map(s => s.amount)
        }];
        this.topSuppliersChartOptions = {
          ...this.topSuppliersChartOptions,
          xaxis: {
            ...this.topSuppliersChartOptions.xaxis,
            categories: data.topSuppliers.map(s => s.name)
          }
        };
      }

      // Update Stacked Aging by Entity
      if (data.agingStacked && data.agingStacked.length > 0) {
        const names = data.agingStacked.map(s => s.name.length > 25 ? s.name.slice(0, 25) + '...' : s.name);
        this.stackedAgingSeries = [
          { name: '0-30 Días', data: data.agingStacked.map(s => parseFloat(s.a0_30) || 0) },
          { name: '31-60 Días', data: data.agingStacked.map(s => parseFloat(s.a31_60) || 0) },
          { name: '61-90 Días', data: data.agingStacked.map(s => parseFloat(s.a61_90) || 0) },
          { name: '+90 Días', data: data.agingStacked.map(s => parseFloat(s.a90plus) || 0) },
        ];
        this.stackedAgingOptions = {
          ...this.stackedAgingOptions,
          xaxis: { ...this.stackedAgingOptions.xaxis, categories: names }
        };
      }

      // Populate the drill-down table with ALL entities (fall back to stacked if table not yet available)
      const tableSource = (data.agingTable && data.agingTable.length > 0) ? data.agingTable : data.agingStacked;
      if (tableSource && tableSource.length > 0) {
        this.agingTableItems = tableSource.map(s => {
          const total = parseFloat(s.total) || 0;
          const a0_30 = parseFloat(s.a0_30) || 0;
          const overdue = total - a0_30;
          return {
            name: s.name,
            total,
            a0_30,
            a31_60: parseFloat(s.a31_60) || 0,
            a61_90: parseFloat(s.a61_90) || 0,
            a90plus: parseFloat(s.a90plus) || 0,
            pct_overdue: total > 0 ? ((overdue / total) * 100).toFixed(1) : '0.0',
          };
        });
      }

      // Update Pareto Chart
      if (data.paretoData && data.paretoData.length > 0) {
        const names = data.paretoData.map(p => p.name.length > 18 ? p.name.slice(0, 18) + '...' : p.name);
        this.paretoChartSeries = [
          { name: 'Monto', type: 'column', data: data.paretoData.map(p => parseFloat(p.amount) || 0) },
          { name: '% Acumulado', type: 'line', data: data.paretoData.map(p => parseFloat(p.cumulative_pct) || 0) },
        ];
        this.paretoChartOptions = {
          ...this.paretoChartOptions,
          xaxis: { ...this.paretoChartOptions.xaxis, categories: names }
        };
      }

      // Update Location Chart
      if (data.locations && data.locations.length > 0) {
        const locNames = data.locations.map(l => l.name.length > 22 ? l.name.slice(0, 22) + '...' : l.name);
        this.locationChartSeries = [{
          name: 'Saldo Pendiente',
          data: data.locations.map(l => parseFloat(l.amount) || 0)
        }];
        this.locationChartOptions = {
          ...this.locationChartOptions,
          xaxis: { ...this.locationChartOptions.xaxis, categories: locNames }
        };
      }

      // Update Trend Chart
      if (data.trend) {
        this.trendChartSeries = [{
          name: 'Facturas Generadas',
          data: data.trend.invoices || []
        }, {
          name: 'Pagos Realizados',
          data: data.trend.payments || []
        }];
        this.trendChartOptions = {
          ...this.trendChartOptions,
          xaxis: {
            ...this.trendChartOptions.xaxis,
            categories: data.trend.months || []
          }
        };
      }

      // Update DPO
      if (data.dpo) {
        this.dpoChartSeries = [data.dpo.value || 0];
      }

      // Update Currency Distribution
      if (data.currencies) {
        this.currencyChartSeries = data.currencies.map(c => c.amount);
        this.currencyChartOptions = {
          ...this.currencyChartOptions,
          labels: data.currencies.map(c => c.code)
        };
      }
    },
    onCurrencyChange(val) {
      this.$emit('currency-change', val);
    },
    onConvertCurrencyChange() {
      if (!this.convertCurrency) {
        this.$emit('convert-currency-change', {
          convert_to: false,
          currency_to: null,
        });
      } else if (this.convertCurrency && this.selectedConvertCurrency) {
        this.$emit('convert-currency-change', {
          convert_to: true,
          currency_to: this.selectedConvertCurrency,
        });
      }
    },
    displayCurrencyCode() {
      if (this.convertCurrency && this.selectedConvertCurrency) {
        const cur = this.allCurrencies.find(c => c.id === this.selectedConvertCurrency);
        return cur ? cur.code : this.currencyName;
      }
      return this.currencyName;
    },
    currencyFormat(value) {
      return '$' + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    fmtNum(val) {
      return parseFloat(val || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    fmtPct(val, total) {
      if (!total || total === 0) return '0.00%';
      return ((val / total) * 100).toFixed(2) + '%';
    },
    fmtDateDMY(iso) {
      if (!iso) return '';
      const [y, m, d] = String(iso).slice(0, 10).split('-');
      return d && m && y ? `${d}/${m}/${y}` : iso;
    },
    // Respaldo: extraer ids del texto de filtros del dashboard si el padre no pasó las props
    parseLegacyFilters() {
      const f = String(this.filters || '');
      const pick = (re) => { const m = f.match(re); return m ? m[1] : null; };
      const access = (pick(/LOCATION_ID IN \(([\d,\s]+)\)/i) || '').split(',').map((x) => parseInt(x)).filter((x) => !isNaN(x));
      return {
        location_id: pick(/\blocation_id\s*=\s*(\d+)/i),
        location_access: access,
        currency_id: pick(/currency_id\s*=\s*(\d+)/i),
      };
    },
    async openPdfOptions() {
      this.pdfOptions.as_of = this.reportDate || new Date().toISOString().slice(0, 10);
      this.pdfOptionsDialog = true;
      if (this.moduleType === 'cxp' && this.supplierTypes.length === 0) {
        this.supplierTypesLoading = true;
        try {
          const res = await this.api_Get('transactions/supplier_types');
          if (res.status === 200) this.supplierTypes = await res.json();
        } catch (e) {
          console.error('Error cargando tipos de suplidor:', e);
        } finally {
          this.supplierTypesLoading = false;
        }
      }
    },
    async generateAgingPDF() {
      this.generatingPdf = true;
      this.pdfUrl = null;
      try {
        const legacy = this.parseLegacyFilters();
        const o = this.pdfOptions;
        const supplierType = this.supplierTypes.find((t) => t.id === o.supplier_type_id);
        const body = {
          module_type: this.moduleType,
          business_unit_id: this.businessId || null,
          location_id: this.locationId ? Number(this.locationId) : (legacy.location_id ? Number(legacy.location_id) : null),
          location_access: this.locationAccess.length ? this.locationAccess : legacy.location_access,
          currency_id: this.convertCurrency ? null : (this.currencyId ?? legacy.currency_id ?? null),
          convert_to_currency_id: this.convertCurrency ? this.selectedConvertCurrency : null,
          as_of: o.as_of,
          date_type: o.date_type,
          due_days: String(o.due_days).split(',').map((x) => parseInt(x)),
          supplier_type_id: o.supplier_type_id || null,
          top_n: o.top_n,
          language: o.language,
          include_detail: !!o.include_detail,
          business_unit_name: this.businessUnitName,
          location_name: this.locationName,
          currency_name: this.currencyName,
          supplier_type_name: supplierType ? supplierType.name : '',
          company_name: this.companyName,
          generated_by: ((this.$store.state.auth.user || {}).user || {}).fullname || '',
        };
        const response = await this.api_Post('transactions/aging_report_pdf', body);
        if (!response.ok) throw new Error('Error al generar el PDF');
        const blob = await response.blob();
        this.pdfUrl = URL.createObjectURL(blob);
        this.pdfOptionsDialog = false;
        this.pdfDialog = true;
      } catch (err) {
        console.error('Error generating Aging Report PDF:', err);
        if (this.$toast) this.$toast.error('Error al generar el PDF del reporte');
      } finally {
        this.generatingPdf = false;
      }
    },
    closePdfDialog() {
      this.pdfDialog = false;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
    },
    downloadPdf() {
      if (!this.pdfUrl) return;
      const link = document.createElement('a');
      link.href = this.pdfUrl;
      link.download = `aging_report_${this.moduleType}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
    }
  }
};
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
